/**
 * The system's controlled vocabularies: roles, permissions, feature flags and
 * the status of each kind of absence.
 *
 * These are closed unions because logic branches on them, but every decoder
 * that reads one from storage maps an unrecognised value to the explicit
 * `UNKNOWN` member instead of throwing. That is what makes a client OTA-safe:
 * when a newer backend introduces a status this build has never heard of, an
 * older client renders it neutrally and keeps working, rather than crashing on
 * an exhaustiveness check in the middle of a list.
 */

/** Rendered wherever a value from storage is not in this build's vocabulary. */
export const UNKNOWN = 'Desconocido' as const;
export type Unknown = typeof UNKNOWN;

// ---------------------------------------------------------------------------
// Roles
// ---------------------------------------------------------------------------

/*
 * Seis roles. `finanzas` y `global` no vienen del prototipo.
 *
 * Existe porque los otros cuatro no saben decir «administra la nómina pero no
 * valida certificados del IMSS». Colgar el salario de `admin` sería lo barato y
 * dejaría que un administrador de área lo viera en cuanto alguien encendiera un
 * permiso de más — sobre el dato más sensible de la base.
 *
 * **Ojo al desplegar:** `Role` está escrito a mano en otros dos sitios que no
 * pueden importar de aquí, `firestore.rules` y `functions/src/shared.ts`. Un rol
 * que exista sólo aquí autentica, lleva claim válida y **no lee nada**, porque
 * ningún predicado de las reglas lo reconoce. Ver docs/KNPEOPLE.md § decisión 3.
 */
export const ROLES = ['empleado', 'lider', 'admin', 'finanzas', 'global', 'super'] as const;
export type Role = (typeof ROLES)[number];

/**
 * Cómo se llama cada rol **en pantalla**, que no es cómo se llama en la base.
 *
 * La llave `empleado` es un identificador: vive en las claims de Auth, en el
 * campo `role` de las 335 fichas, en `config/rolePermissions`, en
 * `firestore.rules` y en `functions/src/shared.ts`. Renombrarla sería una
 * migración de datos con reglas y claims de por medio, y no compra nada.
 *
 * La empresa dice **«Colaborador»**, así que eso es lo que se lee. El resto del
 * portal ya lo decía —las plantillas de correo lo llaman así en `audience`, el
 * certificado del IMSS rotula «Colaborador», y tres pantallas lo escriben en
 * prosa—; esta etiqueta era el último sitio que seguía diciendo «Empleado».
 *
 * Cambiar esta línea cambia los doce sitios que la leen. Lo que **no** cambia es
 * ninguna decisión de autorización: nada compara contra el texto.
 */
export const ROLE_LABEL: Readonly<Record<Role, string>> = {
  finanzas: 'Finanzas',
  empleado: 'Colaborador',
  lider: 'Líder de sub área',
  admin: 'Administrador / RR.HH.',
  global: 'Administrador global',
  super: 'Superusuario',
};

export const isRole = (value: unknown): value is Role =>
  typeof value === 'string' && (ROLES as readonly string[]).includes(value);

/**
 * Quién puede dirigir un equipo: encargado de una sub área, líder de alguien.
 *
 * **No es «quién puede aprobar».** Es más estrecho a propósito: son los roles que
 * firman una ausencia **por el vínculo** de `personLeaders`, que es la única
 * forma de firmar que convierte a alguien en el líder de una persona concreta.
 * `admin` y `super` también deciden ausencias, pero por suplencia o por comodín
 * —cuando no hay líder, o por encima de la cadena—, y ésa es otra cosa.
 *
 * Esta lista existe porque la pregunta se hacía en cuatro sitios de la pantalla
 * de administración con un `role === 'lider'` copiado, y **cambiarla ya salió mal
 * dos veces**:
 *
 * - `role !== 'empleado'` dejaba pasar a `admin` y `super`, y el superusuario
 *   aparecía ofrecido como líder de alguien. No era cosmético: escribía un
 *   vínculo hacia quien no firma por vínculo, y la solicitud se quedaba en una
 *   cola que nadie podía vaciar.
 * - Leerlo de la matriz viva —«quien tenga `aprobar`»— sonaba a principio y era
 *   peor: la matriz se edita desde Configuración global, así que una sola
 *   edición convirtió a las treinta y siete personas del área en candidatas.
 *
 * Por eso es un hecho del rol y una lista cerrada. `global` entró el 28 sep 2026,
 * cuando dejó de ser cierto que ese rol no firma: dirigía a 22 personas, la
 * matriz le concedía `aprobar` y sólo `DECIDING_ROLES` lo frenaba. Si mañana
 * entra otro, que sea escribiéndolo aquí.
 */
export const TEAM_LEAD_ROLES: readonly Role[] = ['lider', 'global'];

export const leadsTeams = (role: Role): boolean => TEAM_LEAD_ROLES.includes(role);

// ---------------------------------------------------------------------------
// Permissions
// ---------------------------------------------------------------------------

export const PERMISSIONS = [
  'solicitar',
  'perfil',
  'calendario_propio',
  'calendario_area',
  'aprobar',
  'ver_saldos',
  'ver_sos',
  'ver_detalle',
  'editar_detalle',
  'reportes',
  'historial',
  'admin',
  'feriados_tab',
  'editar_feriados',
  'config',
  'features',
  'areas',
  /*
   * El salario, partido en ver y editar por la misma razón que `ver_detalle` y
   * `editar_detalle`: son trabajos distintos. Consultarlo para calcular un
   * finiquito no es lo mismo que fijarlo, y quien calcula no tiene por qué poder
   * cambiarlo.
   */
  'ver_salario',
  'editar_salario',
  /*
   * El alta, partida en dos por la misma razón que el salario: son dos trabajos.
   *
   * `alta_persona` captura y deja la ficha esperando; `validar_cuenta` acepta lo
   * que otro capturó — y quien lo tiene, además, no necesita que le validen lo
   * suyo. Con un solo permiso no había forma de decir «esta persona da altas pero
   * no las aprueba», que es justo el control que se pidió.
   */
  'alta_persona',
  'validar_cuenta',
  /*
   * El expediente personal —estado civil, INFONAVIT, estudios, tipo de sangre,
   * alergias, correo y teléfono personales—, partido igual que el salario.
   *
   * Permisos propios y no `ver_detalle`: ése lo tienen los líderes porque
   * necesitan la ficha de su equipo para el calendario y las aprobaciones, y
   * colgar de él el expediente les habría dado las alergias y el estado civil de
   * su gente sin que nadie lo decidiera.
   */
  'ver_expediente',
  'editar_expediente',
  /*
   * Administrar los tipos de incapacidad y de permiso **de su área**.
   *
   * Separado de `config`, que es del superusuario y gobierna la empresa entera:
   * quien dirige un área sabe si ahí se reparte home office, y no por eso tiene
   * que poder tocar las reglas de vacaciones de las otras siete. El alcance del
   * área lo impone la callable con `requireScope`; esto sólo decide quién puede.
   */
  'catalogos_area',
  /*
   * Ajustar el saldo, aparte de `editar_detalle`.
   *
   * Corregir un nombre mal escrito y otorgarle días a alguien son trabajos
   * distintos, y hasta ahora los gobernaba el mismo permiso: quien arreglaba una
   * errata podía cambiarle el derecho a una persona. Los dos números que toca
   * —`openingUsedDays` y `entitledDaysOverride`— mueven directamente cuántos días
   * puede pedir.
   */
  'ajustar_saldo',
  /*
   * El organigrama de un área, partido en ver y armar — y los dos se reparten
   * distinto, a propósito.
   *
   * Verlo es de un tipo de puesto: la matriz lo da por rol, como casi todo.
   * Armarlo **no se da por rol**, y ningún rol lo trae de fábrica: es el dibujo
   * que la empresa enseña de sí misma, y quién lo cambia se decide persona por
   * persona desde su ficha. Dárselo a un rol se lo daría a todos los que lo
   * tienen, que es justo lo que se pidió evitar.
   */
  'ver_organigrama',
  'editar_organigrama',
] as const;
export type Permission = (typeof PERMISSIONS)[number];

export const isPermission = (value: unknown): value is Permission =>
  typeof value === 'string' && (PERMISSIONS as readonly string[]).includes(value);

export const PERMISSION_GROUPS = [
  'Uso personal',
  'Gestión',
  'Administración',
  'Superusuario',
] as const;
export type PermissionGroup = (typeof PERMISSION_GROUPS)[number];

export interface PermissionDescriptor {
  readonly key: Permission;
  readonly label: string;
  readonly note: string;
  readonly group: PermissionGroup;
}

/**
 * The permission catalogue, verbatim from the handoff. This describes what each
 * permission *means* — it is presentation metadata and is intentionally part of
 * the build. Which role *holds* which permission is runtime configuration and
 * lives in `config/rolePermissions`.
 */
export const PERMISSION_CATALOG: readonly PermissionDescriptor[] = [
  {
    key: 'solicitar',
    label: 'Solicitar vacaciones',
    note: 'Abrir el asistente y enviar solicitudes propias',
    group: 'Uso personal',
  },
  {
    key: 'perfil',
    label: 'Ver su perfil y saldo',
    note: 'Historial propio, días disponibles y documentos',
    group: 'Uso personal',
  },
  {
    key: 'calendario_propio',
    label: 'Calendario propio',
    note: 'Ver únicamente sus propias ausencias',
    group: 'Uso personal',
  },
  {
    key: 'calendario_area',
    label: 'Calendario del área',
    note: 'Ver quién está fuera en todas las sub áreas',
    group: 'Gestión',
  },
  {
    key: 'aprobar',
    label: 'Aprobar y rechazar',
    note: 'Bandeja de decisiones y visto bueno',
    group: 'Gestión',
  },
  {
    key: 'ver_saldos',
    label: 'Ver saldos del equipo',
    note: 'Días disponibles de otras personas',
    group: 'Gestión',
  },
  {
    key: 'ver_sos',
    label: 'Ver contactos de emergencia',
    note: 'Teléfonos de familiares en tarjetas y personal del área',
    group: 'Gestión',
  },
  {
    key: 'ver_detalle',
    label: 'Ver detalle de una persona',
    note: 'Abrir su ficha desde cualquier avatar: datos, antigüedad y saldo',
    group: 'Gestión',
  },
  {
    key: 'editar_detalle',
    label: 'Corregir datos de una persona',
    note: 'Nombre, correo, fecha de ingreso y los días que se le contaron',
    group: 'Administración',
  },
  {
    key: 'reportes',
    label: 'Reportes del área',
    note: 'Métricas de uso, riesgo y tendencias',
    group: 'Gestión',
  },
  {
    key: 'historial',
    label: 'Bitácora',
    note: 'Expediente de movimientos y documentos',
    group: 'Gestión',
  },
  {
    key: 'admin',
    label: 'Sub áreas y cuentas',
    note: 'Crear sub áreas, validar cuentas, asignar líderes',
    group: 'Administración',
  },
  {
    key: 'feriados_tab',
    label: 'Ver feriados del país',
    note: 'Pestaña del calendario oficial',
    group: 'Administración',
  },
  {
    key: 'editar_feriados',
    label: 'Editar feriados',
    note: 'Agregar, apagar o quitar días oficiales',
    group: 'Administración',
  },
  {
    key: 'config',
    label: 'Configuración global',
    note: 'Identidad, reglas, catálogos y correos',
    group: 'Superusuario',
  },
  {
    key: 'features',
    label: 'Features y módulos',
    note: 'Encender o apagar capacidades del sistema',
    group: 'Superusuario',
  },
  {
    key: 'areas',
    label: 'Áreas de la empresa',
    note: 'Crear áreas padre y asignar administradores',
    group: 'Superusuario',
  },
  {
    key: 'ajustar_saldo',
    label: 'Ajustar el saldo de una persona',
    note: 'Días ya gastados fuera del portal y derecho por contrato. Cambia cuántos días puede pedir',
    group: 'Administración',
  },
  {
    key: 'alta_persona',
    label: 'Dar de alta a una persona',
    note: 'Capturar el alta. Queda esperando a que alguien más la valide',
    group: 'Administración',
  },
  {
    key: 'validar_cuenta',
    label: 'Validar altas y cuentas',
    note: 'Aceptar lo que otro capturó — y que lo propio no necesite validación',
    group: 'Administración',
  },
  {
    key: 'ver_salario',
    label: 'Ver el salario de una persona',
    note: 'Necesario para calcular un finiquito. Es el dato más sensible del portal',
    group: 'Administración',
  },
  {
    key: 'editar_salario',
    label: 'Fijar el salario de una persona',
    note: 'Registrar o corregir el sueldo mensual del que salen los cálculos',
    group: 'Administración',
  },
  {
    key: 'ver_expediente',
    label: 'Ver el expediente personal',
    note: 'Estado civil, INFONAVIT, estudios, tipo de sangre, alergias y sus datos de contacto',
    group: 'Administración',
  },
  {
    key: 'editar_expediente',
    label: 'Corregir el expediente personal',
    note: 'Cambiar lo que la persona contestó en el formulario de datos',
    group: 'Administración',
  },
  {
    key: 'catalogos_area',
    label: 'Incapacidades y permisos de su área',
    note: 'Elegir qué tipos se ofrecen en su área y dar de alta los suyos',
    group: 'Administración',
  },
  {
    key: 'ver_organigrama',
    label: 'Ver el organigrama',
    note: 'Consultar quién le reporta a quién en las áreas que ve',
    group: 'Gestión',
  },
  {
    key: 'editar_organigrama',
    label: 'Armar el organigrama',
    note: 'Crear y cambiar el dibujo del área. Se da persona por persona, no por rol',
    group: 'Administración',
  },
];

/**
 * Factory defaults, used to seed `config/rolePermissions` and to back the
 * "Restaurar original" button. Never consulted at runtime for an authorization
 * decision — that always reads the live matrix, so an edit takes effect without
 * a redeploy.
 */
/**
 * The starting matrix, copied verbatim from the prototype's `PERMS`.
 *
 * Two entries are easy to get wrong and both are deliberate:
 *
 *  - An **empleado** holds `calendario_area`, not just `calendario_propio`.
 *    Seeing who else is already out is the whole premise of the product — the
 *    login screen promises exactly that — so withholding it would break the
 *    pitch, not merely trim a permission.
 *  - A **super** does not hold `solicitar` ni `aprobar`. A superuser configures
 *    the company; they do not file requests or approve them. Granting them
 *    everything would put sections in their sidebar that the design never shows
 *    them.
 *  - A **super** does not hold `admin` either, and getting there took a detour
 *    worth recording. The prototype's matrix deadlocked the portal: `admin` was
 *    what `validatePendingAccount` demanded, the seeder creates exactly one
 *    account and it is a superuser, and every account after it is born
 *    `pending` — so nobody could validate the first one and no `admin` role
 *    could ever come to exist. Granting the superuser `admin` broke the
 *    deadlock and smuggled in a passenger: `admin` also validates medical
 *    certificates, so the superuser inherited RR.HH.'s board under «Gestión del
 *    área», a heading for an area they do not have.
 *
 *    The account callables now accept `admin` **or** `areas`, which is the split
 *    `createSubArea` already used — an area administrator shapes their own area,
 *    a superuser shapes any. The superuser reaches accounts through `areas`,
 *    which they already hold, and certificates stay where they belong.
 *
 * `ver_detalle` y `editar_detalle` son la única pareja que **no** sale del
 * prototipo, porque la pantalla que gobiernan tampoco: el prototipo tiene el
 * perfil propio y ninguna ficha administrativa de otra persona. Se separan en dos
 * a propósito. Mirar la ficha de alguien de tu equipo es parte de dirigirlo;
 * reescribirle la fecha de ingreso mueve su antigüedad, su derecho y la etiqueta
 * de su saldo, y eso es trabajo de quien administra el padrón. Un líder que
 * necesite una corrección la pide, que es lo mismo que ya ocurre con el rol y la
 * sub área.
 *
 * This is only the default. The matrix is runtime-editable, and the security
 * floor lives in the Firestore rules, not here — so changing this file does not
 * change a database that has already been seeded. The live document is
 * `config/rolePermissions`.
 */
export const DEFAULT_ROLE_PERMISSIONS: Readonly<Record<Role, readonly Permission[]>> = {
  empleado: ['solicitar', 'perfil', 'calendario_area', 'calendario_propio'],
  lider: [
    'solicitar',
    'perfil',
    'calendario_area',
    'aprobar',
    'reportes',
    'historial',
    'ver_saldos',
    'ver_sos',
    'ver_detalle',
    'ver_organigrama',
  ],
  /*
   * Finanzas: el salario y lo mínimo para llegar a una ficha, y nada más.
   *
   * Sin `aprobar`, sin `admin` y sin `solicitar` — no decide ausencias, no valida
   * cuentas ni certificados, y no se le dan permisos «por si acaso». Es el rol que
   * existe para acotar, así que dárselo todo lo vaciaría de sentido.
   */
  finanzas: ['perfil', 'ver_detalle', 'ver_salario', 'editar_salario'],
  admin: [
    'alta_persona',
    'validar_cuenta',
    'catalogos_area',
    'perfil',
    'calendario_area',
    'ver_expediente',
    'editar_expediente',
    'reportes',
    'historial',
    'admin',
    'ver_saldos',
    'ver_sos',
    'ver_detalle',
    'editar_detalle',
    'ajustar_saldo',
    'ver_organigrama',
  ],
  /*
   * Administrador global: RR.HH. sin frontera de área, y sin firmar.
   *
   * Existe porque `admin` sabe decir «administra a su gente» y `super` sabe decir
   * «configura la plataforma», y hacía falta un tercero: quien tiene que ver el
   * estado de las ocho áreas —quién está fuera, quién no tiene líder, cómo va la
   * cobertura— sin poder cambiar reglas, feriados ni permisos.
   *
   * **Sin `aprobar`, y es lo que lo define.** Firmar unas vacaciones es decir
   * «puedes faltar y tu trabajo queda cubierto», y eso lo sabe quien dirige el
   * equipo, no quien mira la empresa entera. Un rol que puede firmar en las ocho
   * áreas salta la cadena de mando sin que se note en ninguna pantalla.
   *
   * Sin `features`, `areas`, `config` ni `editar_feriados`: mira la operación, no
   * la plataforma. Y `solicitar` sí, porque también es una persona con vacaciones.
   */
  global: [
    'alta_persona',
    'validar_cuenta',
    'solicitar',
    'perfil',
    'calendario_propio',
    'calendario_area',
    'ver_saldos',
    'ver_sos',
    'ver_detalle',
    'editar_detalle',
    'ver_expediente',
    'editar_expediente',
    'reportes',
    'historial',
    'admin',
    'ver_organigrama',
  ],
  super: [
    'alta_persona',
    'validar_cuenta',
    'catalogos_area',
    'features',
    'areas',
    'ver_expediente',
    'editar_expediente',
    'config',
    'historial',
    'editar_feriados',
    'feriados_tab',
    'ver_detalle',
    'editar_detalle',
    'ajustar_saldo',
    'ver_organigrama',
  ],
};

/**
 * Permissions a role may never lose, because losing one would lock the holder
 * out of the screen that grants it back. The handoff states this for the
 * superuser and `features`: strip it and the permissions screen itself becomes
 * unreachable, with no way in short of editing the database by hand.
 */
export const PROTECTED_PERMISSIONS: Readonly<Partial<Record<Role, readonly Permission[]>>> = {
  super: ['features'],
};

// ---------------------------------------------------------------------------
// Feature flags
// ---------------------------------------------------------------------------

export const FEATURES = [
  'incapacidades',
  'imss',
  'permisos',
  'feriados',
  'reportes',
  'historial',
  'kanban',
  'reemplazo',
  'doblevb',
  'busqueda',
  'knblog',
  'whatsapp',
] as const;
export type FeatureKey = (typeof FEATURES)[number];

export const isFeatureKey = (value: unknown): value is FeatureKey =>
  typeof value === 'string' && (FEATURES as readonly string[]).includes(value);

export interface FeatureDescriptor {
  readonly key: FeatureKey;
  readonly label: string;
  readonly note: string;
  readonly group: 'Módulos' | 'Gestión' | 'Reglas';
  /** A feature that is meaningless unless its dependency is also on. */
  readonly dependsOn?: FeatureKey;
  readonly color: string;
}

export const FEATURE_CATALOG: readonly FeatureDescriptor[] = [
  {
    key: 'incapacidades',
    label: 'Módulo de incapacidades',
    note: 'Registro y seguimiento de ausencias médicas',
    group: 'Módulos',
    color: '#E8930C',
  },
  {
    key: 'imss',
    label: 'Validación con IMSS',
    note: 'Botón de validación y captura de folio del certificado',
    group: 'Módulos',
    dependsOn: 'incapacidades',
    color: '#E8930C',
  },
  {
    key: 'permisos',
    label: 'Home office y permisos',
    note: 'Días remotos, permisos de horas y días personales',
    group: 'Módulos',
    color: '#12A594',
  },
  {
    key: 'feriados',
    label: 'Calendario de feriados',
    note: 'Días oficiales visibles y descontados del cálculo',
    group: 'Módulos',
    color: '#7C5CFF',
  },
  {
    key: 'reportes',
    label: 'Reportes del área',
    note: 'Métricas de uso para líderes y RR.HH.',
    group: 'Gestión',
    color: '#4A6FA5',
  },
  {
    key: 'historial',
    label: 'Bitácora de movimientos',
    note: 'Registro de eventos y documentos archivados',
    group: 'Gestión',
    color: '#4A6FA5',
  },
  {
    key: 'kanban',
    label: 'Tablero kanban de aprobaciones',
    note: 'Arrastrar tarjetas entre columnas de estado',
    group: 'Gestión',
    color: '#7C5CFF',
  },
  {
    key: 'reemplazo',
    label: 'Reemplazo obligatorio',
    note: 'Bloquea el envío sin designar quién cubre',
    group: 'Reglas',
    color: '#38619F',
  },
  {
    key: 'doblevb',
    label: 'Doble visto bueno',
    note: 'Cuando el área tiene más de 2 líderes, un segundo líder debe validar la aprobación',
    group: 'Reglas',
    color: '#38619F',
  },
  {
    key: 'busqueda',
    label: 'Búsqueda global',
    note: 'Buscador en la barra superior',
    group: 'Reglas',
    color: '#5C6B80',
  },
  {
    key: 'knblog',
    label: 'Kn·Blog',
    note: 'Cumpleaños de la empresa, primero en el menú de todos',
    group: 'Módulos',
    color: '#A21C5A',
  },
  {
    key: 'whatsapp',
    label: 'Asistente de WhatsApp',
    note: 'Consultar y pedir vacaciones por chat, con el número vinculado desde Mi perfil',
    group: 'Módulos',
    color: '#12A594',
  },
];

export const DEFAULT_FEATURES: Readonly<Record<FeatureKey, boolean>> = {
  incapacidades: true,
  imss: true,
  permisos: true,
  feriados: true,
  reportes: true,
  historial: true,
  kanban: true,
  reemplazo: true,
  doblevb: true,
  busqueda: true,
  /*
   * Apagado de fábrica: sale primero en el menú de las 331
   * personas, así que se enciende cuando alguien lo decide en Configuración
   * global › Features — no con el despliegue. El servidor pide además un `true`
   * explícito en `config/featureFlags` (`requireFeature`), así que sin ese clic
   * tampoco contesta.
   */
  knblog: false,
  /*
   * Apagado de fábrica también, y por más motivo que Kn·Blog: encenderlo abre un
   * segundo canal por el que se piden y se aprueban vacaciones, y le manda a
   * Meta —y, si se configura, a un modelo de lenguaje— lo que la gente escriba.
   * Lo decide una persona en Configuración global › Features; el servidor pide el
   * `true` explícito y además el secreto `WHATSAPP_BOT`, así que el despliegue
   * solo no contesta a nadie.
   */
  whatsapp: false,
};

// ---------------------------------------------------------------------------
// Vacation request status
// ---------------------------------------------------------------------------

export const REQUEST_STATUSES = [
  'Borrador',
  'Por revisar',
  'En revisión',
  'Visto bueno',
  'Aprobada',
  'Rechazada',
  'Cancelada',
  'Tomada',
  UNKNOWN,
] as const;
export type RequestStatus = (typeof REQUEST_STATUSES)[number];

export const isRequestStatus = (value: unknown): value is RequestStatus =>
  typeof value === 'string' && (REQUEST_STATUSES as readonly string[]).includes(value);

/**
 * Statuses whose holder counts as out of office **for the concurrency cap**.
 *
 * Deliberately wide: the cap asks "how much of this sub area could be away on
 * that day", and a request nobody has decided yet is precisely the thing a leader
 * needs to see before adding another. Answering it with approvals only would
 * clear a week that three people have already asked for.
 *
 * It is the wrong list for anything that reports the present — see
 * `CONFIRMED_ABSENCE_STATUSES`.
 */
export const OCCUPYING_STATUSES: readonly RequestStatus[] = [
  'Por revisar',
  'En revisión',
  'Visto bueno',
  'Aprobada',
  'Tomada',
];

/**
 * Statuses whose holder is actually away — a decision was made.
 *
 * The distinction is not pedantry, and it cost a QA round to find. «Fuera hoy»
 * and «Fuera esta semana» were built on `OCCUPYING_STATUSES`, so a colleague who
 * had merely *asked* for Friday off was reported as already gone: the tile said
 * seven people were out on a day two were, and the greeting counted five
 * colleagues away when two of the five were still waiting on a signature.
 *
 * The rule that separates them: a **forecast** — what could collide, what a
 * leader should weigh — uses `OCCUPYING_STATUSES`. A **statement about who is
 * away** uses this one. A screen that says someone is out when nobody approved it
 * is not being cautious, it is wrong.
 */
export const CONFIRMED_ABSENCE_STATUSES: readonly RequestStatus[] = ['Aprobada', 'Tomada'];

/** Statuses past which no further decision is possible. */
export const TERMINAL_STATUSES: readonly RequestStatus[] = [
  'Aprobada',
  'Rechazada',
  'Cancelada',
  'Tomada',
];

export const isTerminalStatus = (status: RequestStatus): boolean =>
  TERMINAL_STATUSES.includes(status);

/** The kanban board's columns, in order, with the design's accent per column. */
export const BOARD_COLUMNS = [
  { status: 'Por revisar', color: '#7C5CFF' },
  { status: 'En revisión', color: '#E8930C' },
  { status: 'Visto bueno', color: '#38619F' },
  { status: 'Aprobada', color: '#12A594' },
  { status: 'Rechazada', color: '#E5484D' },
] as const satisfies readonly { status: RequestStatus; color: string }[];

// ---------------------------------------------------------------------------
// Permission-request (home office) and incapacity status
// ---------------------------------------------------------------------------

export const PERMISSION_REQUEST_STATUSES = [
  'Por aprobar',
  'Aprobado',
  'Rechazado',
  'Cancelado',
  UNKNOWN,
] as const;
export type PermissionRequestStatus = (typeof PERMISSION_REQUEST_STATUSES)[number];

export const isPermissionRequestStatus = (value: unknown): value is PermissionRequestStatus =>
  typeof value === 'string' && (PERMISSION_REQUEST_STATUSES as readonly string[]).includes(value);

/** Permits still waiting on a leader's decision. */
export const AWAITING_PERMISSION_STATUSES: readonly PermissionRequestStatus[] = ['Por aprobar'];

/**
 * The permit board's columns.
 *
 * Deliberately not the vacation columns. A permit has no «Visto bueno» because it
 * takes one signature, and no «En revisión» because there is nothing to review
 * between filing and deciding — forcing it into the vacation board would show two
 * columns that can never hold a card.
 */
export const PERMIT_BOARD_COLUMNS = [
  { status: 'Por aprobar', color: '#E8930C' },
  { status: 'Aprobado', color: '#12A594' },
  { status: 'Rechazado', color: '#E5484D' },
  { status: 'Cancelado', color: '#64748B' },
] as const satisfies readonly { status: PermissionRequestStatus; color: string }[];

export const INCAPACITY_STATUSES = [
  'Por validar',
  'Validada',
  'Observada',
  'Programada',
  /**
   * Retirada por quien la registró.
   *
   * Un certificado mal capturado —fechas equivocadas, el archivo de otra
   * persona, un duplicado— no se corrige editándolo: el registro original es
   * parte del expediente y borrarlo dejaría un hueco sin explicación. Se retira
   * y se vuelve a registrar, y esto es lo que dice que ya no cuenta.
   */
  'Cancelada',
  UNKNOWN,
] as const;
export type IncapacityStatus = (typeof INCAPACITY_STATUSES)[number];

export const isIncapacityStatus = (value: unknown): value is IncapacityStatus =>
  typeof value === 'string' && (INCAPACITY_STATUSES as readonly string[]).includes(value);

/**
 * Certificates still waiting on somebody.
 *
 * `Observada` belongs here alongside `Por validar`: a certificate RR.HH. returned
 * as illegible is not settled, it is back with the employee. Counting only
 * `Por validar` made the sidebar pill disagree with the tab it points at — the
 * kind of drift these shared constants exist to prevent.
 */
export const AWAITING_INCAPACITY_STATUSES: readonly IncapacityStatus[] = [
  'Por validar',
  'Observada',
];

/**
 * The certificate board's columns.
 *
 * «Observada» sits second rather than last because it is not an outcome: it is a
 * certificate that came back and is waiting to be resent. Putting it beside the
 * closed states would read as settled.
 */
export const CERTIFICATE_BOARD_COLUMNS = [
  { status: 'Por validar', color: '#E8930C' },
  { status: 'Observada', color: '#7C5CFF' },
  { status: 'Validada', color: '#12A594' },
  { status: 'Programada', color: '#38619F' },
  { status: 'Cancelada', color: '#64748B' },
] as const satisfies readonly { status: IncapacityStatus; color: string }[];

// ---------------------------------------------------------------------------
// Account lifecycle
//
// A new corporate account authenticates but starts with no role: it is
// `pending` until an area administrator validates it. That is the handoff's
// "Cuentas por validar" queue, and it is why a valid Google sign-in alone
// grants access to nothing.
// ---------------------------------------------------------------------------

/**
 * Cómo trabaja alguien. Catálogo cerrado de tres, no texto libre.
 *
 * El organigrama de esta empresa salió de un campo de texto libre con 99 grafías
 * para unos cuarenta nombres, y costó una tarde de desempates. Tres valores que
 * se pueden contar y filtrar —«cuántos remotos hay en Comercial»— valen más que
 * la libertad de escribir «hibrido» sin acento.
 */
/**
 * Los documentos que RR.HH. pide al dar de alta.
 *
 * Salen de la hoja con la que se dan las altas hoy, y coinciden con los seis que
 * el formulario de la familia Knotion ya recogió como enlaces de Drive en 326
 * respuestas — más la licencia, que allí sólo tenían 106 de 326. Por eso es la
 * única opcional: exigirla convertiría en incompleta a dos de cada tres fichas
 * el día que se estrene la lista.
 *
 * El orden es el de la hoja, no el alfabético: quien captura los tiene delante en
 * ese orden y va tachando.
 */
export const PERSON_DOCUMENTS = [
  { key: 'formato', label: 'Formato de nuevo ingreso', required: true },
  { key: 'acta', label: 'Acta de nacimiento', required: true },
  { key: 'domicilio', label: 'Comprobante de domicilio', required: true },
  { key: 'fiscal', label: 'Constancia de situación fiscal', required: true },
  { key: 'nss', label: 'Constancia de asignación de NSS', required: true },
  { key: 'curp', label: 'CURP', required: true },
  { key: 'ine', label: 'INE (ambos lados)', required: true },
  { key: 'licencia', label: 'Licencia de conducir', required: false },
] as const;

export type PersonDocumentKind = (typeof PERSON_DOCUMENTS)[number]['key'];

export const isPersonDocumentKind = (value: unknown): value is PersonDocumentKind =>
  typeof value === 'string' && PERSON_DOCUMENTS.some((doc) => doc.key === value);

export const WORK_MODES = ['presencial', 'hibrido', 'remoto'] as const;
export type WorkMode = (typeof WORK_MODES)[number];

export const WORK_MODE_LABEL: Readonly<Record<WorkMode, string>> = {
  presencial: 'Presencial',
  hibrido: 'Híbrido',
  remoto: 'Remoto',
};

export const isWorkMode = (value: unknown): value is WorkMode =>
  typeof value === 'string' && (WORK_MODES as readonly string[]).includes(value);

/**
 * Las tallas de playera, para la ropa que la empresa reparte.
 *
 * Seis, y salen de contar el padrón de RR.HH., no de suponer: de las 288
 * personas con talla anotada, 285 caen en cinco de éstas —M 112, L 84, S 49,
 * XL 29, XXL 10— y la sexta, XS, la trae una sola. Ninguna hoja usa nada por
 * encima de XXL.
 *
 * **El rótulo traduce el código a propósito.** El archivo de origen mezcla los
 * dos idiomas —junto a las inglesas vienen «CH», «C» y «G»— y quien captura no
 * tiene por qué saber si «L» es larga o grande. La llave se queda en inglés
 * porque es la que la gente dice en voz alta al pedir su playera.
 */
export const SHIRT_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL'] as const;
export type ShirtSize = (typeof SHIRT_SIZES)[number];

export const SHIRT_SIZE_LABEL: Readonly<Record<ShirtSize, string>> = {
  XS: 'XS — Extra chica',
  S: 'S — Chica',
  M: 'M — Mediana',
  L: 'L — Grande',
  XL: 'XL — Extra grande',
  XXL: 'XXL — Doble extra grande',
};

export const isShirtSize = (value: unknown): value is ShirtSize =>
  typeof value === 'string' && (SHIRT_SIZES as readonly string[]).includes(value);

/**
 * Las tallas en español que la carga tiene que saber leer.
 *
 * La escalera mexicana —chico, mediano, grande, extra grande— convive con la
 * inglesa en el mismo libro y a veces en la misma hoja. Se traduce sólo lo que
 * significa una única cosa.
 */
const SHIRT_SIZE_ALIASES: Readonly<Record<string, ShirtSize>> = {
  ECH: 'XS',
  CH: 'S',
  G: 'L',
  XG: 'XL',
  EG: 'XL',
  XXG: 'XXL',
};

/**
 * Lee una talla escrita a mano y devuelve la del catálogo, o `null`.
 *
 * **`null` no es un fallo, es un hueco**, y es información: dice a quién hay que
 * preguntarle. Una talla adivinada, en cambio, no se distingue de una
 * contestada — que es la forma que este repositorio ya pagó cinco veces con los
 * valores que nadie mantiene.
 *
 * Por eso hay tres cosas que devuelven `null` a propósito:
 *
 * - **`-`**, que tres personas de Tecnología escribieron. Es «no aplica», no una
 *   talla.
 * - **`C`**, que una persona escribió. Al lado hay «CH» y «G» en la misma hoja,
 *   así que probablemente sea chico — pero *probablemente* no basta para
 *   ponerle una talla a alguien, y una letra suelta también puede ser un dedazo.
 * - Cualquier cosa que no esté en el catálogo ni en los alias.
 */
export const parseShirtSize = (value: unknown): ShirtSize | null => {
  if (typeof value !== 'string') return null;
  const limpio = value.trim().toUpperCase();
  if (isShirtSize(limpio)) return limpio;
  return SHIRT_SIZE_ALIASES[limpio] ?? null;
};

/**
 * Si es papá o mamá, y nada más que eso.
 *
 * **No es un dato de género y no debe convertirse en uno.** Lo único que hay que
 * poder contestar es a quién felicitar en mayo y a quién en junio; guardar el
 * género en la ficha —que cualquier líder de su área puede leer— sería recoger
 * un dato más sensible que el que hace falta, para el mismo fin.
 *
 * De dónde sale: el libro de RR.HH. trae dos columnas, «Papá» y «Mamá», con
 * 41 y 86 marcas. **Nadie está marcado en las dos**, así que las dos columnas son
 * en realidad un campo con tres valores, y así se guarda.
 *
 * `null` es el caso de 211 de 338 personas y significa «no consta» —ni que no
 * tenga hijos ni que no quiera decirlo—. La cuenta de hijos sigue viviendo en
 * `PersonalRecord.children`, que es expediente y está cerrado por reglas; esto
 * es sólo la parte que hace falta que se pueda leer.
 */
export const PARENT_ROLES = ['papa', 'mama'] as const;
export type ParentRole = (typeof PARENT_ROLES)[number];

export const PARENT_ROLE_LABEL: Readonly<Record<ParentRole, string>> = {
  papa: 'Papá',
  mama: 'Mamá',
};

export const isParentRole = (value: unknown): value is ParentRole =>
  typeof value === 'string' && (PARENT_ROLES as readonly string[]).includes(value);

export const PERSON_STATUSES = ['pending', 'active', 'inactive', UNKNOWN] as const;
export type PersonStatus = (typeof PERSON_STATUSES)[number];

export const isPersonStatus = (value: unknown): value is PersonStatus =>
  typeof value === 'string' && (PERSON_STATUSES as readonly string[]).includes(value);

// ---------------------------------------------------------------------------
// Holidays
// ---------------------------------------------------------------------------

export const HOLIDAY_TYPES = ['Obligatorio', 'Por costumbre', UNKNOWN] as const;
export type HolidayType = (typeof HOLIDAY_TYPES)[number];

export const isHolidayType = (value: unknown): value is HolidayType =>
  typeof value === 'string' && (HOLIDAY_TYPES as readonly string[]).includes(value);

// ---------------------------------------------------------------------------
// Audit log
// ---------------------------------------------------------------------------

export const AUDIT_KINDS = [
  'Solicitud',
  'Aprobación',
  'Documento',
  'Política',
  'Cuenta',
  'Liderazgo',
  UNKNOWN,
] as const;
export type AuditKind = (typeof AUDIT_KINDS)[number];

export const isAuditKind = (value: unknown): value is AuditKind =>
  typeof value === 'string' && (AUDIT_KINDS as readonly string[]).includes(value);

/** Scope a rejection-reason catalogue entry applies to. */
export const REJECTION_SCOPES = ['vacaciones', 'permiso', 'incapacidad', 'cancelacion'] as const;
export type RejectionScope = (typeof REJECTION_SCOPES)[number];

export const isRejectionScope = (value: unknown): value is RejectionScope =>
  typeof value === 'string' && (REJECTION_SCOPES as readonly string[]).includes(value);
